import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import api from 'api';
import useTranslate from 'hooks/useTranslate';
import { setAlert } from 'redux/alert';
import type { AppDispatch } from 'redux/store';
import type { AssessmentBillingRule, ServiceProviderAssessmentBillingConfig } from 'types';
import { getErrorMessage } from 'utils/v2/errorHelper';

export function useAssessmentBilling(providerId: number | null) {
    const dispatch = useDispatch<AppDispatch>();
    const { translate } = useTranslate();
    const [config, setConfig] = useState<ServiceProviderAssessmentBillingConfig | null>(null);
    const [fetching, setFetching] = useState(false);
    const [loadedId, setLoadedId] = useState<number | null>(null);
    const loading = fetching || (providerId != null && loadedId !== providerId);

    useEffect(() => {
        setConfig(null);
        if (providerId == null) {
            setFetching(false);
            setLoadedId(null);
            return;
        }
        let cancelled = false;
        const load = async () => {
            setFetching(true);
            try {
                const response =
                    await api.serviceProviderBilling.getAssessmentBillingConfig(providerId);
                if (!cancelled) {
                    setConfig(response.data);
                }
            } catch (error) {
                if (!cancelled) {
                    dispatch(
                        setAlert({
                            message: getErrorMessage(
                                error,
                                translate('serviceProviderBilling.assessment.loadFailed')
                            ),
                            type: 'error',
                        })
                    );
                }
            } finally {
                if (!cancelled) {
                    setFetching(false);
                    setLoadedId(providerId);
                }
            }
        };
        void load();
        return () => {
            cancelled = true;
        };
    }, [dispatch, providerId, translate]);

    const save = async (assessment: AssessmentBillingRule | null): Promise<boolean> => {
        if (providerId == null) {
            return false;
        }
        try {
            const response = await api.serviceProviderBilling.saveAssessmentBillingConfig({
                service_provider_id: providerId,
                assessment,
            });
            setConfig(response.data);
            dispatch(
                setAlert({
                    message: translate('serviceProviderBilling.assessment.saved'),
                    type: 'success',
                })
            );
            return true;
        } catch (error) {
            dispatch(
                setAlert({
                    message: getErrorMessage(
                        error,
                        translate('serviceProviderBilling.assessment.saveFailed')
                    ),
                    type: 'error',
                })
            );
            return false;
        }
    };

    return { config, loading, save };
}
